import { invariant } from '@mimir/common/util/invariant';
import axios from 'axios';
import { optDebugLog } from '../util/debug';
import type { AdobePanelContext } from '@mimir/common/context/applicationContext';

/**
 * There is code in this file to route communication with Adobe apps through IFrame postmessage API when Mimir is
 * loaded in an IFrame.
 * Those can be removed when we can make sure that all customers have upgraded to panel version 3.1.0 or later,
 * which loads the panel in the main window of the panel and does not need an IFrame.
 */

export enum SystemPath {
  /** The path to user data.  */
  USER_DATA = 'userData',

  /** The path to common files for Adobe applications.  */
  COMMON_FILES = 'commonFiles',

  /** The path to the user's default document folder.  */
  MY_DOCUMENTS = 'myDocuments',

  /** @deprecated. Use \c #Extension.  */
  APPLICATION = 'application',

  /** The path to current extension.  */
  EXTENSION = 'extension',

  /** The path to hosting application's executable.  */
  HOST_APPLICATION = 'hostApplication'
}

// An increasing integer ID is used to make message ID generation as fast as possible.
// Start with current milliseconds, to make message IDs unique across logins.
let messageIdCounter = Date.now();

export const isInTopFrame = () => window.top === window;
export const routeViaParentFrame = () => {
  if ((window as any).debug_directRouting) {
    return false;
  }
  const cepVersion = getCepApiMajorVersion();
  const topFrame = isInTopFrame();
  const parentRouting = cepVersion >= 11 && !topFrame;
  optDebugLog('routeViaParentFrame', { cepVersion, topFrame, parentRouting });
  return parentRouting;
};

export const evalScriptInternal = (script: string, callback?: any) => {
  if (!hasAdobePanel()) {
    console.warn('evalScript not available');
    if (callback) {
      callback();
    }
    return;
  }
  if (callback === null || callback === undefined) {
    callback = (_: any) => {};
  }

  if (!routeViaParentFrame()) {
    // before CEP 11. or when we return to the top level from a redirect after AD login.
    window.__adobe_cep__.evalScript(script, callback);
  } else {
    // CEP 11 or later
    const messageId = `${messageIdCounter++}`;
    const cbMap = getCallbackMap();
    cbMap[messageId] = callback;
    postMessageToParent({
      messageId,
      payload: script,
      type: 'EVAL_SCRIPT_REQUEST',
    });
  }
};

type CepInterFrameMessageId = string;
type CepInterFramePayload = string;
type CepInterFrameMessageType = 'EVAL_SCRIPT_REQUEST' | 'EVAL_SCRIPT_RESPONSE' | 'EVENT_SUBCRIPTION_REQUEST' | 'EVENT_DATA';
interface CepInterFrameMessage {
  messageId: CepInterFrameMessageId;
  payload: CepInterFramePayload;
  type: CepInterFrameMessageType;
}

// With CEP 11, evalScript is only available on the main frame. So we need to send the script
// we want to execute to main frame with the postMessage API.
const postMessageToParent = (message: CepInterFrameMessage) => {
  window.parent.postMessage(message, '*');
};

// This is for handling any results returned from the evalScript execution
// sent from the main frame via the postMessage API. It is also used for
// handling other events when project/sequence changes, or local AME completes.
export const parentMessageHandler = (event: MessageEvent) => {
  const message = event.data as CepInterFrameMessage;
  if (!message || !message.messageId) {
    console.warn(`Skipping unrecognized message: ${message}`);
    return;
  }

  const cbMap = callbackMap;
  invariant(cbMap != null, 'Expected initialized callbackMap');
  const callback = cbMap[message.messageId];
  if (!callback) {
    console.warn(`Skipping message without callback: ${message.messageId}`);
    return;
  }

  callback(message.payload);
  if (message.type === 'EVAL_SCRIPT_RESPONSE') {
    delete cbMap[message.messageId];
  }
};

// This map keeps track of which callback to call for which message
let callbackMap: Record<CepInterFrameMessageId, any> | null = null;

function getCallbackMap(): Record<CepInterFrameMessageId, any> {
  if (!callbackMap) {
    window.addEventListener('message', parentMessageHandler);
    callbackMap = {};
  }
  return callbackMap;
}

export function hasAdobePanel(): boolean {
  return window.__adobe_cep__ != null;
}

export const addEventListener = (type: any, listener: any) => {
  if (!routeViaParentFrame()) {
    // before CEP 11. or when we return to the top level from a redirect after AD login.
    window.__adobe_cep__.addEventListener(type, listener, undefined);
  } else {
    // CEP 11 or later
    addHostFrameEventListener(type, listener);
  }
};

const addHostFrameEventListener = (type: any, listener: any) => {
  const messageId = `${messageIdCounter++}`;
  const cbMap = getCallbackMap();
  cbMap[messageId] = listener;
  postMessageToParent({
    messageId,
    payload: type,
    type: 'EVENT_SUBCRIPTION_REQUEST',
  });
};

export const getSystemPathWithProtocolPrefix = (pathType: SystemPath) => {
  return decodeURI(window.__adobe_cep__.getSystemPath(pathType));
};

export const openURLInDefaultBrowserFromAdobePanel = (url: string) => {
  window.cep.util.openURLInDefaultBrowser(url);
};

const getAppId = () => {
  const hostEnv = window.__adobe_cep__ && JSON.parse(window.__adobe_cep__.getHostEnvironment());
  return hostEnv?.appId;
};

enum AdobeApps {
  Premiere = 'PPRO',
  AfterEffects = 'AEFT',
  Photoshop = 'PHSP',
  PhotoshopExt = 'PHXS',
  Audition = 'AUDT',
}

export const isPremiere = () => {
  return getAppId() === AdobeApps.Premiere;
};

export const isAe = () => {
  return getAppId() === AdobeApps.AfterEffects;
};

export const isPs = () => {
  const appId = getAppId();
  return appId === AdobeApps.Photoshop || appId === AdobeApps.PhotoshopExt;
};

export const isAudition = () => {
  return getAppId() === AdobeApps.Audition;
};

export const getAppVersion = () => {
  const hostEnv = window.__adobe_cep__ && JSON.parse(window.__adobe_cep__.getHostEnvironment());
  return hostEnv?.appVersion;
};

export async function knowsAboutAME(): Promise<boolean> {
  const result = await new Promise((resolve) => {
    window.__adobe_cep__.evalScript('app.encoder', resolve);
  });
  return result == '[object Encoder]';
}

interface CepVersion {
  major: string;
  minor: string;
  micro: string;
}
export const getCepApiVersion = () => {
  const apiVersion: CepVersion | undefined = window.__adobe_cep__ && JSON.parse(window.__adobe_cep__.getCurrentApiVersion());
  return apiVersion;
};

let cachedCepApiMajorVersion = -1;
export const getCepApiMajorVersion = () => {
  if (cachedCepApiMajorVersion >= 0) {
    return cachedCepApiMajorVersion;
  }

  const apiVersion = getCepApiVersion();
  if (!apiVersion) {
    return cachedCepApiMajorVersion;
  }

  cachedCepApiMajorVersion = parseInt(apiVersion.major);
  return cachedCepApiMajorVersion;
};

export const evalScript = (functionCall: string, callback?: (result: any) => void, errorCallback?: (cause: any) => void) => {
  let tryBlockExtra = '';
  let finallyBlockExtra = '';
  const appId = getAppId();
  optDebugLog('evalScript appId', appId);
  if (appId === AdobeApps.Premiere) {
    tryBlockExtra = `
      // suppress noise from project change events while we are busy doing our own thing
      MimirPanel.ignoreProjectChanges = true;
    `;

    finallyBlockExtra = `
      // restore project change events
      MimirPanel.ignoreProjectChanges = false;

      // if we have suppressed any event in the mean time, send one to continue now
      if (MimirPanel.suppressedProjectChangeEvents > 0) {
        MimirPanel.suppressedProjectChangeEvents = 0;
        MimirPanel.sendProjectChangedEvent();
      }
    `;
  }

  const errorPrefix = 'Mimir Panel Error';
  let wrapperScript = `
    var result;
    try {
      ${tryBlockExtra}

      result = MimirPanel.${functionCall};
    } catch (errMimir) {
      result = '${errorPrefix} on ' + ${JSON.stringify(functionCall)} + ': errMimir: ' + errMimir
    } finally {
      ${finallyBlockExtra}
    }
    result;
  `;

  const wrapperCallback = (result: any) => {
    optDebugLog('evalScript callback result', { result });
    if (result && (typeof result === 'string') && result.startsWith(errorPrefix)) {
      console.error(result);
      errorCallback && errorCallback(result);
    } else {
      callback && callback(result);
    }
  };

  optDebugLog('evalScript wrapperScript', wrapperScript);
  if ((window as any).debug_nowrap) {
    wrapperScript = `MimirPanel.${functionCall}`;
  }
  evalScriptInternal(wrapperScript, wrapperCallback);
};

let initializedMimirPanelScriptPath: string | null = null;

export async function initializeMimirPremierePanelIfNeeded() {
  if (initializedMimirPanelScriptPath != 'assets/premiere/premiere.jsx') {
    await initializeExtendScript('assets/premiere/premiere.jsx');
    evalScript('initializeStatic()');
  }
}

/**
 * Fetches the ExtendScript code from server needed for Premiere-specific interactions and 'eval's it
 * in order to have the functions installed in place to be called during subsequent workflows.
 *
 * JSON is not available by default. so we import an implementation for it at runtime,
 * since #include directives inside ExtendScript no longer work in the New World.
 */
export const initializeExtendScript = async (scriptRelativePath: string) => {
  try {
    const siteRoot = window.location.origin;
    const timestamp = new Date().getTime();
    console.debug(`siteRoot: ${siteRoot}`);

    const promises = [
      axios.get(`${siteRoot}/${scriptRelativePath}?ts=${timestamp}`),
      axios.get(`${siteRoot}/assets/premiere/json2.min.js?ts=${timestamp}`),
    ];

    const [jsxResponse, json2Response] = await Promise.all(promises);
    await evalRawScript(json2Response.data);
    await evalRawScript(jsxResponse.data);
  } catch (err) {
    console.error(`initializeExtendScript: ${err}`);
    throw err;
  }
  initializedMimirPanelScriptPath = scriptRelativePath;
};

export function evalPremiereMimirPanel(functionCall: string, callback?: (result: any) => void, errorCallback?: (cause: any) => void) {
  initializeMimirPremierePanelIfNeeded().then(_ => {
    evalScript(functionCall, callback, errorCallback);
  }).catch(errorCallback);
}

export const evalRawScript = async (script: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const scriptCallback = (result: any) => {
      console.debug(`evalRawScript result: ${result}`);
      resolve(result);
    };

    evalScriptInternal(script, scriptCallback);
  });
};

interface KeyInterest {
  keyCode: number,
  ctrlKey?: boolean;
  altKey?: boolean;
  shiftKey?: boolean;
  metaKey?: boolean;
}

export const registerShortcuts = (adobePanelContext: AdobePanelContext) => {
  const cepInput: KeyInterest[] = [];
  if (adobePanelContext.isMac) {
    // Mac Key Codes: https://eastmanreference.com/complete-list-of-applescript-key-codes
    // Only registering keys that are needed for playback and transcript editing, as well as jumping to a v-select value.
    const numbers = [18, 19, 20, 21, 23, 22, 26, 28, 25, 29];
    cepInput.push(...numbers.map(keyCode => { return { keyCode }; }));

    const alphabet = [0, 11, 8, 2, 14, 3, 5, 4, 34, 38, 40, 37, 46, 45, 31, 35, 12, 15, 1, 17, 32, 9, 13, 7, 16, 6];
    cepInput.push(...alphabet.map(keyCode => { return { keyCode }; }));
    cepInput.push(...alphabet.map(keyCode => { return { keyCode, shiftKey: true }; }));

    const keyCodes = [
      43,  // ,
      47,  // .
      36,  // return
      76,  // enter
      51,  // backspace
      115, // home
      119, // end
      117, // delete
      123, // left
      124, // right
      125, // down
      126, // up
      58,  // left option
      61,  // right option
      59,  // control
      49,  // space
      53,  // esc
    ];
    cepInput.push(...keyCodes.map(keyCode => { return { keyCode }; }));

    cepInput.push(
      // alt+space for play/pause from transcript editor
      {
        keyCode: 49,
        altKey: true,
      },
      // command+alt+shift+t for enabling/disabling the unit test panel
      {
        keyCode: 17,
        altKey: true,
        metaKey: true,
        shiftKey: true,
      },
      // ctrl+alt+space for seeking from transcript editor (not working)
      {
        keyCode: 49,
        altKey: true,
        ctrlKey: true,
      },
      // ctrl+s for saving edits
      {
        keyCode: 1, // s
        ctrlKey: true,
      },
      // ctrl+u for inserting into sequence
      {
        keyCode: 32, // u
        ctrlKey: true,
      },
      // ctrl+alt+t to jump to a timecode
      {
        keyCode: 17,
        altKey: true,
        ctrlKey: true,
      },
      // shift+ctrl+option+o to open an item
      {
        keyCode: 31,
        altKey: true,
        ctrlKey: true,
        shiftKey: true,
      },
      {
        keyCode: 125,
        shiftKey: true,
      },
      {
        keyCode: 126,
        shiftKey: true,
      },
      {
        keyCode: 7,
        altKey: true,
      },
      // command + A for select all
      {
        keyCode: 0,
        metaKey: true,
      },
    );
  } else {
    // Windows Key Codes: https://docs.microsoft.com/en-gb/windows/win32/inputdev/virtual-key-codes
    for (let i = 48; i <= 57; i++) {
      cepInput.push({ keyCode: i });
    }
    for (let i = 65; i <= 90; i++) {
      cepInput.push({ keyCode: i });
      cepInput.push({ keyCode: i, shiftKey: true });
    }
    const keyCodes = [
      0x0D, // enter
      0x08, // backspace
      0x2E, // delete
      0x25, // left
      0x26, // up
      0x27, // right
      0x28, // down
      0x20, // space
      0x1B, // esc
    ];
    cepInput.push(...keyCodes.map(keyCode => { return { keyCode }; }));
  }
  window.__adobe_cep__.registerKeyEventsInterest(JSON.stringify(cepInput));
};

export async function getFresh(url: string) {
  const timestamp = new Date().getTime();
  const response = await axios.get(`${url}?ts=${timestamp}`);
  return response.data;
}

